/**
 * Global focus-return for dialogs/overlays — an accessibility fix.
 *
 * Vuetify's `VDialog` only restores focus on close when it was opened through a registered
 * `activatorEl` (the `activator` slot/prop). Every dialog in this app is driven by `v-model`
 * with a separate trigger button, so no activator is registered and, on close, focus falls
 * through to `<body>`. Keyboard and screen-reader users are then dropped at the top of the
 * document and must tab all the way back to the button that opened the dialog.
 *
 * Wiring focus restoration into ~60 individual dialogs would be fragile, so this installs a
 * single document-level manager instead:
 *
 *  1. It remembers the last element focused OUTSIDE any overlay — the trigger (usually a button).
 *  2. When focus leaves a dialog's content and lands on `<body>` (i.e. the overlay closed and
 *     nothing else claimed focus), it returns focus to that remembered trigger.
 *
 * It deliberately does nothing when focus lands somewhere meaningful on its own — Vuetify
 * returned focus to an activator, a menu handed focus back to its select, the user clicked
 * another control, or a nested dialog kept focus trapped in the parent overlay — so it only
 * repairs the broken `<body>` case and never fights Vuetify's correct behaviour.
 */

// Vuetify's overlay content wrapper (dialogs, menus, selects, autocompletes all render into it).
const OVERLAY_CONTENT_SELECTOR = '.v-overlay__content'

// The last focusable element focused outside any overlay — the presumed dialog trigger.
let lastTrigger: HTMLElement | null = null

function isInsideOverlay(el: Element | null): boolean {
  return !!el?.closest(OVERLAY_CONTENT_SELECTOR)
}

// A trigger is only worth returning focus to if it still exists, is focusable, and is not the
// body itself (restoring focus to <body> would be a no-op that defeats the purpose).
function isRestorableTrigger(el: HTMLElement | null): el is HTMLElement {
  return (
    !!el &&
    el !== document.body &&
    el.isConnected &&
    typeof el.focus === 'function'
  )
}

function onFocusIn(event: FocusEvent): void {
  const target = event.target as HTMLElement | null
  // Only track focus that lands outside overlays — that is the opener, not dialog internals.
  if (!target || isInsideOverlay(target) || target === document.body) return
  lastTrigger = target
}

function onFocusOut(event: FocusEvent): void {
  const from = event.target as HTMLElement | null
  // React only when focus is leaving the inside of an overlay (a closing dialog). Focus moving
  // around the page, or into a dialog as it opens, is none of our business.
  if (!isInsideOverlay(from)) return

  // Let Vuetify's own close handling (nextTick → activatorEl focus) and any user-driven focus
  // settle first, then repair only if focus fell through to <body>.
  requestAnimationFrame(() => {
    const active = document.activeElement as HTMLElement | null
    const focusLandedNowhere = !active || active === document.body
    // Something already holds focus (an activator, another control, or a still-open parent
    // overlay) — leave it alone.
    if (!focusLandedNowhere || isInsideOverlay(active)) return
    if (isRestorableTrigger(lastTrigger)) {
      lastTrigger.focus()
    }
  })
}

/**
 * Install the global dialog focus-return manager. Call once from `main.ts`. No-op in a
 * non-DOM environment (SSR / unit tests without jsdom).
 */
export function installDialogFocusReturn(): void {
  if (typeof document === 'undefined') return
  // Capture phase so we see focus events before they are potentially stopped, and so
  // focusin/focusout from deep within overlays still reach us.
  document.addEventListener('focusin', onFocusIn, true)
  document.addEventListener('focusout', onFocusOut, true)
}
